import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2 } from "lucide-react";
import { GoogleButton } from "@/components/GoogleButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[#0A2E26] noise-overlay">
        <img
          src="https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Home"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative p-12 h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#C49A45] flex items-center justify-center">
              <Building2 className="w-4 h-4 text-[#0A2E26]" />
            </div>
            <span className="font-display font-bold text-lg text-white">EquiBrick</span>
          </Link>
          <div>
            <h2 className="font-display font-bold text-3xl text-white leading-snug">
              Welcome back to<br />the fund.
            </h2>
            <p className="text-white/60 mt-3">Track your shares, returns and portfolio.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-[#F9F8F6]">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h1 className="font-display font-bold text-3xl text-[#0A2E26]">Log in</h1>
          <p className="text-[#5C5C5C] mt-2">Access your investor dashboard.</p>

          {error && (
            <div data-testid="login-error" className="mt-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 bg-white" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 bg-white" />
            </div>
          </div>

          <Button type="submit" data-testid="login-submit" disabled={loading}
            className="w-full mt-6 bg-[#0A2E26] hover:bg-[#07201A] text-white rounded-md h-11">
            {loading ? "Logging in…" : "Log in"}
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-[#E5E5E5]" />
            <span className="text-xs text-[#5C5C5C]">or</span>
            <div className="h-px flex-1 bg-[#E5E5E5]" />
          </div>
          <GoogleButton label="Log in with Google" />

          <p className="text-sm text-[#5C5C5C] mt-5 text-center">
            No account?{" "}
            <Link to="/register" data-testid="to-register" className="text-[#0A2E26] font-semibold hover:underline">
              Become a member
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
